/**
 * Station Breakdown Grid Component
 *
 * World-class pharmaceutical analytics grid showing maturity scores for all 7 stations:
 * - Data Quality
 * - Live Checks (FAIR Assessment)
 * - Interviews
 * - Documents
 * - ML Readiness
 * - Controlled Vocabularies
 * - Ontology Inference
 *
 * Each card features:
 * - Circular gauge (120px diameter)
 * - Score (0-100) with classification color
 * - Classification badge
 * - Confidence percentage
 * - Sparkline trend (if historical data available)
 *
 * Design: Executive-ready, pharmaceutical standards
 * Spec: Documentation/Development/Design Docs/Maturity_Dashboard_UI_UX_Specification.md
 */

import React from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import type { NormalizedScore } from '../../server/scoring/maturityNormalizer';

// ============================================================================
// TYPES
// ============================================================================

interface StationBreakdownGridProps {
  stationScores: {
    data_quality?: NormalizedScore;
    live_checks?: NormalizedScore;
    interviews?: NormalizedScore;
    documents?: NormalizedScore;
    ml_readiness?: NormalizedScore;
    controlled_vocabularies?: NormalizedScore;
    ontology_inference?: NormalizedScore;
  };
}

// ============================================================================
// STATION METADATA
// ============================================================================

const STATION_META: Record<string, { name: string; icon: string }> = {
  data_quality: { name: 'Data Quality', icon: '📊' },
  live_checks: { name: 'Live Checks', icon: '🔍' },
  interviews: { name: 'Interviews', icon: '🎤' },
  documents: { name: 'Documents', icon: '📄' },
  ml_readiness: { name: 'ML Readiness', icon: '🤖' },
  controlled_vocabularies: { name: 'Controlled Vocabularies', icon: '🏷️' },
  ontology_inference: { name: 'Ontology Inference', icon: '🧬' }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get score color based on maturity band
 */
function getScoreColor(score: number): string {
  if (score >= 90) return '#27AE60';      // Excellent
  if (score >= 75) return '#3498DB';      // Good
  if (score >= 60) return '#F39C12';      // Acceptable
  if (score >= 40) return '#E67E22';      // Fair
  if (score >= 20) return '#E74C3C';      // Poor
  return '#C0392B';                       // Critical
}

/**
 * Get classification label
 */
function getClassificationLabel(classification: string): string {
  return classification.toUpperCase();
}

// ============================================================================
// SPARKLINE COMPONENT
// ============================================================================

interface SparklineProps {
  data: number[];
  color: string;
}

const Sparkline: React.FC<SparklineProps> = ({ data, color }) => {
  if (!data || data.length < 2) return null;

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((value - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg
      className="sparkline"
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        opacity="0.6"
      />
    </svg>
  );
};

// ============================================================================
// STATION CARD COMPONENT
// ============================================================================

interface StationCardProps {
  stationId: string;
  score: NormalizedScore;
  trendData?: number[];  // Historical scores for sparkline
  onClick?: () => void;
}

const StationCard: React.FC<StationCardProps> = ({
  stationId,
  score,
  trendData,
  onClick
}) => {
  const meta = STATION_META[stationId];
  if (!meta) {
    console.warn(`[StationCard] Unknown station: ${stationId}`);
    return null;
  }

  // Defensive: ensure score is a number (API may return strings)
  const scoreValue = typeof score.score === 'number' ? score.score : parseFloat(String(score.score));
  const confidenceValue = typeof score.confidence === 'number' ? score.confidence : parseFloat(String(score.confidence));

  // Safety check
  if (isNaN(scoreValue)) {
    console.error(`[StationCard] Invalid score for ${stationId}:`, score);
    return null;
  }

  const scoreColor = getScoreColor(scoreValue);
  const classificationLabel = getClassificationLabel(score.classification);
  const confidencePercent = !isNaN(confidenceValue) ? (confidenceValue * 100).toFixed(0) : '0';

  return (
    <div className="station-card" onClick={onClick}>
      <h3 className="station-name">
        <span className="station-icon">{meta.icon}</span>
        {meta.name}
      </h3>

      {/* Circular Gauge */}
      <div className="station-score-visual">
        <CircularProgressbar
          value={scoreValue}
          text={`${Math.round(scoreValue)}`}
          strokeWidth={12}
          styles={buildStyles({
            pathColor: scoreColor,
            textColor: '#ffffff',
            trailColor: '#3d4152',
            textSize: '32px',
            pathTransitionDuration: 0.8,
          })}
        />
      </div>

      {/* Classification Badge */}
      <div className="station-classification" style={{ color: scoreColor }}>
        {classificationLabel}
      </div>

      {/* Confidence */}
      <div className="station-confidence">
        Confidence: {confidencePercent}%
      </div>

      {/* Sparkline Trend (if available) */}
      {trendData && trendData.length > 1 && (
        <div className="station-trend">
          <Sparkline data={trendData} color={scoreColor} />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const StationBreakdownGrid: React.FC<StationBreakdownGridProps> = ({ stationScores }) => {
  const stations = Object.entries(stationScores)
    .filter(([_, score]) => score !== undefined)
    .map(([id, score]) => ({ id, score: score! }));

  if (stations.length === 0) {
    return (
      <div className="station-breakdown-empty">
        <div className="empty-icon">📊</div>
        <div className="empty-message">No station scores available yet.</div>
        <div className="empty-hint">Complete stations to see breakdown.</div>
      </div>
    );
  }

  return (
    <div className="station-breakdown-section">
      <div className="section-header">
        <h2>Station Breakdown</h2>
        <div className="section-subtitle">
          {stations.length} of 7 stations completed
        </div>
      </div>

      <div className="station-breakdown-grid">
        {stations.map(({ id, score }) => (
          <StationCard
            key={id}
            stationId={id}
            score={score}
            trendData={undefined}  // TODO: Add historical trend data when available
            onClick={() => {
              // TODO: Navigate to station details
              console.log(`Navigate to station: ${id}`);
            }}
          />
        ))}
      </div>
    </div>
  );
};
